import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge, TipoBadge } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { money, dateOnly, fmtClabe, KYC_LABEL } from "@/lib/format";

const TIPOS = [["", "Todos"], ["PM", "Persona Moral"], ["PF", "Persona Física"]];

export default function Clients() {
  const api = useApi();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/clients", { params: { q, tipo, kyc_status: status === "all" ? "" : status } }).then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(errMsg(e)); });
    }, 250);
    return () => clearTimeout(t);
  }, [api, q, tipo, status]);

  return (
    <>
      <PageHeader title="Clientes" subtitle="Personas Morales y Físicas registradas en e-Plata">
        <Button asChild className="bg-teal-600 hover:bg-teal-700" data-testid="clients-new-btn"><Link to="/clientes/nuevo"><UserPlus className="mr-2 h-4 w-4" />Alta de cliente</Link></Button>
      </PageHeader>
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="clients-search-input" className="pl-9" placeholder="Buscar por nombre, razón social o RFC" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex rounded-lg border border-slate-200 p-1">
          {TIPOS.map(([v, l]) => (
            <button key={v || "all"} data-testid={`filter-tipo-${v || "todos"}`} onClick={() => setTipo(v)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${tipo === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-kyc-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estatus</SelectItem>
            {Object.entries(KYC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[820px] text-sm" data-testid="clients-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">RFC</th><th className="px-4 py-3">CLABE</th><th className="px-4 py-3 text-right">Saldo</th><th className="px-4 py-3">KYC</th><th className="px-4 py-3">Alta</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows === null && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="clients-empty">No hay clientes con esos filtros</td></tr>}
            {rows?.map((c) => (
              <tr key={c.id} onClick={() => nav(`/clientes/${c.id}`)} data-testid={`client-row-${c.id}`} className="cursor-pointer transition-colors hover:bg-teal-50/40">
                <td className="px-4 py-3.5"><p className="font-medium text-slate-900">{c.nombre}</p><p className="text-xs text-slate-500">{c.email}</p></td>
                <td className="px-4 py-3.5"><TipoBadge tipo={c.tipo} /></td>
                <td className="px-4 py-3.5 font-mono text-xs">{c.rfc}</td>
                <td className="px-4 py-3.5 font-mono text-xs">{c.clabe ? fmtClabe(c.clabe) : <span className="text-slate-400">Sin cuenta</span>}</td>
                <td className="px-4 py-3.5 text-right font-mono">{money(c.saldo)}</td>
                <td className="px-4 py-3.5"><StatusBadge status={c.kyc_status} label={KYC_LABEL[c.kyc_status]} /></td>
                <td className="px-4 py-3.5 text-slate-500">{dateOnly(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
